/**
 * CORE-1901 — 429 «слишком много запросов» не сбой приложения, а просьба сервера
 * подождать.
 *
 * Лимитер API — фиксированное окно в минуту (300 запросов на пользователя, 500
 * на IP гостя; `RateLimitingSetup.cs` бэкенда). Отказ снимается сам, когда окно
 * сменится, поэтому экран комнаты на 429 не рисует «эфир недоступен» и не шлёт
 * событие в GlitchTip, а повторяет загрузку с паузой: не короче `Retry-After`,
 * если ответ его донёс, и с растущей лестницей, если нет.
 *
 * `Retry-After` браузер отдаёт коду страницы только при CORS `Access-Control-
 * Expose-Headers` — на вебе с другого домена его может не быть, тогда пауза
 * берётся из лестницы. Её верх — минута: дольше одно окно не держит.
 *
 * Чистые функции — проверяются jest без экрана.
 */

/** Код тела 429 от бэкенда (`ErrorCodes.RateLimitExceeded`). */
export const RATE_LIMIT_CODE = 'RATE_LIMIT_EXCEEDED';

/** Пауза перед повтором номер `attempt` (0 — первый), без разброса. */
export const RATE_LIMIT_RETRY_STEPS_MS = [2_000, 5_000, 10_000, 20_000, 30_000, 60_000];

/** Ни одна пауза не длиннее этого, даже если сервер попросил больше. */
export const RATE_LIMIT_RETRY_MAX_MS = 120_000;

type ErrLike = { status?: unknown; code?: unknown; message?: unknown; retryAfterMs?: unknown } | null | undefined;

/**
 * Отказ — это лимит запросов? `status` 429 у ошибок API-клиента, `code` из тела
 * ответа, а у билета хаба (обычная `Error`, которую SignalR может обернуть в
 * «Failed to start the connection: …») — текст `HTTP 429`.
 */
export function isRateLimitedError(err: unknown): boolean {
  const e = err as ErrLike;
  if (!e || (typeof e !== 'object' && typeof e !== 'string')) return false;
  if (typeof e === 'object') {
    if (e.status === 429) return true;
    if (e.code === RATE_LIMIT_CODE) return true;
  }
  const message = typeof e === 'string' ? e : typeof e.message === 'string' ? e.message : '';
  return /\bHTTP 429\b/.test(message) || message.includes(RATE_LIMIT_CODE);
}

/**
 * Значение заголовка `Retry-After` в миллисекундах: число секунд или
 * HTTP-дата. Нет заголовка, мусор, прошедшая дата — `null`.
 */
export function parseRetryAfterMs(header: string | null | undefined, now: number = Date.now()): number | null {
  if (header == null) return null;
  const value = String(header).trim();
  if (!value) return null;
  if (/^\d+(\.\d+)?$/.test(value)) return Math.round(Number(value) * 1000);
  const at = Date.parse(value);
  if (Number.isNaN(at)) return null;
  const ms = at - now;
  return ms > 0 ? ms : null;
}

/** `Retry-After`, приложенный к ошибке API-клиентом (`retryAfterMs`), или `null`. */
export function retryAfterMsOf(err: unknown): number | null {
  const v = (err as ErrLike)?.retryAfterMs;
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null;
}

/**
 * Пауза перед повтором после 429. Ступень лестницы с разбросом ±25 % (чтобы
 * клиенты, упёршиеся в лимит разом, не вернулись волной), но не короче
 * `Retry-After`. `random` — в [0, 1), подменяется в тестах.
 */
export function rateLimitRetryDelayMs(
  attempt: number,
  retryAfterMs: number | null = null,
  random: () => number = Math.random,
): number {
  const steps = RATE_LIMIT_RETRY_STEPS_MS;
  const base = steps[Math.min(Math.max(Math.floor(attempt) || 0, 0), steps.length - 1)];
  const jittered = Math.round(base * (0.75 + 0.5 * random()));
  const floor = retryAfterMs != null && retryAfterMs > 0 ? retryAfterMs : 0;
  return Math.min(Math.max(jittered, floor), RATE_LIMIT_RETRY_MAX_MS);
}

/**
 * Нижняя граница паузы для пересборки хаба: `Retry-After` отказа, если отказ —
 * лимит и сервер назвал срок; иначе 0 (пауза остаётся за лестницей хаба).
 */
export function rateLimitFloorMs(err: unknown): number {
  if (!isRateLimitedError(err)) return 0;
  return Math.min(retryAfterMsOf(err) ?? 0, RATE_LIMIT_RETRY_MAX_MS);
}

/**
 * CORE-1901 — можно ли назначить ещё один автоповтор после 429, если уже
 * сделано `retriesDone` автоповторов, а предел цикла — `maxRequests` запросов
 * ВСЕГО, вместе с исходным (или вызванным кнопкой «Повторить»). Предел 30 —
 * это 1 исходный запрос + 29 автоповторов, а не 30 повторов сверху.
 */
export function canAutoRetryRateLimit(retriesDone: number, maxRequests: number): boolean {
  return retriesDone + 1 < maxRequests;
}
